"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordSchema } from "@/schemas/auth/auth.schema";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useMutationQuery } from "@/hooks/useMutationQuery";
import { useTranslation } from "@/hooks/useLanguage";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [emailSent, setEmailSent] = useState(false);
  const [alreadySent, setAlreadySent] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const { handleApiAction, isLoading } = useMutationQuery();

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
    mode: "onTouched",
  });

  const handleSubmit = async (data) => {
    const loading = isLoading("forgotPassword");
    if (loading) return;

    try {
      const response = await handleApiAction("forgotPassword", data);

      // Response structure: { data: { success, message, data: { alreadySent }, ... }, config }
      const apiResponse = response?.data;

      if (apiResponse?.success) {
        const message =
          apiResponse.message ||
          "If an account exists with this email, a password reset link has been sent.";
        const responseData = apiResponse.data || {};
        setAlreadySent(Boolean(responseData.alreadySent));
        setFeedbackMessage(message);
        setEmailSent(true);
        toast({
          type: "success",
          title: "Email Sent",
          message,
        });
      } else {
        console.warn("Forgot password response not successful:", apiResponse);
        toast({
          type: "warning",
          title: "Response Received",
          message:
            apiResponse?.message ||
            "Please check your email. If you don't receive it, please try again or contact support.",
        });
        setEmailSent(true);
      }
    } catch (err) {
      toast({
        type: "error",
        title: "Error",
        message: err?.message || "An error occurred. Please try again.",
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <div className="flex w-full order-first items-center justify-center bg-muted p-8 md:order-last md:w-1/2">
        <div className="flex flex-col items-center text-center">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-primary/10 mb-5">
            <Mail className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-4 text-primary">
            {t("forgotPassword.pageTitle")}
          </h1>
          <p className="text-foreground">
            {t("forgotPassword.pageDescription")}
          </p>
        </div>
      </div>
      <div className="flex w-full flex-col justify-center bg-background p-6 md:w-1/2">
        <div className="mx-auto w-full max-w-md">
          {emailSent ? (
            <>
              <h2 className="text-3xl font-semibold text-foreground mb-4">
                {alreadySent
                  ? t("forgotPassword.alreadySentTitle")
                  : t("forgotPassword.checkEmailTitle")}
              </h2>
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-5 mb-6">
                <p className="text-green-700 dark:text-green-400">
                  <strong>
                    {alreadySent
                      ? t("forgotPassword.alreadySentLabel")
                      : t("forgotPassword.successLabel")}
                  </strong>
                  <br />
                  {feedbackMessage || t("forgotPassword.defaultSuccessMessage")}
                </p>
              </div>
              <Link href="/auth">
                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 flex items-center justify-center"
                >
                  <ArrowLeft className="h-5 w-5 me-2" />
                  {t("forgotPassword.returnToLogin")}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-semibold text-foreground mb-4">
                {t("common.forgot_password")}
              </h2>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-5"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t("common.email")}
                            className="w-full rounded-full border border-border p-6 focus:border-primary"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    disabled={isLoading("forgotPassword")}
                    className="w-full rounded-full py-6"
                  >
                    {isLoading("forgotPassword")
                      ? t("common.sending")
                      : t("forgotPassword.sendResetLink")}
                  </Button>
                </form>
              </Form>
              <div className="mt-6 text-center">
                <Link
                  href="/auth"
                  className="flex items-center justify-center text-primary hover:text-primary/80 font-medium text-sm"
                >
                  <ArrowLeft className="h-4 w-4 me-2" />
                  {t("common.back_to_login")}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
